import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { AppContext } from '../../context/Context';
import SpilleplanScreensaver from './SpilleplanScreensaver';

// The self-service waiting screen, shared by LoginKiosk and SalgsKiosk.
//
// Today's gameplan is the screensaver; the video is the fallback, shown when
// there is no plan to draw — a closed day, an empty link, a socket that never
// answers. Was IdleVideoOverlay under views/LoginKiosk, video only.
function IdleOverlay({ idleMs = 30000, videoSrc = '', activitySignal = 0 }) {
  const [isIdle, setIsIdle] = useState(false);
  // Set by the screensaver itself when the plan comes back empty
  const [planEmpty, setPlanEmpty] = useState(false);
  const timeoutRef = useRef(null);
  const isIdleRef = useRef(false);

  const {
    updateShowModalEmptyHall,
    updateShowModalWrongKioskUser,
    updateShowModalTicketInfoKiosk,
    updateTicketInfoKiosk,

    updateShowModalPapirGameBatchResult,
    updateShowModalPapirGameNotFinished,
    updateShowModalPapirGameFinishedWithoutPrize,
    updateShowModalPapirGameFinishedWithPrize,
    updateShowModalPapirCheckCard,
  } = useContext(AppContext);

  useEffect(() => {
    isIdleRef.current = isIdle;
  }, [isIdle]);

  const resetTimer = useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(() => {
      isIdleRef.current = true;
      setIsIdle(true);

      updateShowModalEmptyHall(false);
      updateShowModalWrongKioskUser(false);
      updateShowModalTicketInfoKiosk(false);
      updateTicketInfoKiosk(false);

      updateShowModalPapirGameBatchResult(false);
      updateShowModalPapirGameNotFinished(false);
      updateShowModalPapirGameFinishedWithoutPrize(false);
      updateShowModalPapirGameFinishedWithPrize(false);
      updateShowModalPapirCheckCard(false);
    }, idleMs);
  }, [idleMs]);

  const handleActivity = useCallback(() => {
    if (isIdleRef.current) {
      isIdleRef.current = false;
      setIsIdle(false);
    }

    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    const events = ['pointerdown', 'touchstart', 'keydown', 'mousemove'];

    events.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, { passive: true });
    });

    resetTimer();

    return () => {
      events.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });

      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [handleActivity, resetTimer]);

  useEffect(() => {
    handleActivity();
  }, [activitySignal, handleActivity]);

  // Latched, not re-asked on every wake: a day with no games stays that way,
  // and the plan is refetched inside the screensaver anyway
  const onEmpty = useCallback(() => setPlanEmpty(true), []);

  if (!isIdle) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2000,
        backgroundColor: '#050505',
      }}
    >
      {!planEmpty && <SpilleplanScreensaver onEmpty={onEmpty} />}

      {planEmpty && videoSrc ? (
        <video
          src={videoSrc}
          autoPlay
          muted
          loop
          playsInline
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : null}
    </div>
  );
}

export default IdleOverlay;
